import { mergeAttributes, Node } from "@tiptap/core"

import type { JSONContent } from "@tiptap/core"
import type { DOMOutputSpec } from "@tiptap/pm/model"

export interface GalleryImage {
	src: string
	alt: string | null
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		imageGallery: {
			setImageGallery: (options: { images: GalleryImage[] }) => ReturnType
		}
	}
}

export const IMAGE_GALLERY_NAME = "imageGallery"

export const parseGalleryImages = (element: HTMLElement): GalleryImage[] =>
	[...element.querySelectorAll("img")].reduce<GalleryImage[]>((images, img) => {
		const src = img.getAttribute("src")
		if (src) images.push({ src, alt: img.getAttribute("alt") })
		return images
	}, [])

export const toGalleryImages = (value: unknown): GalleryImage[] => {
	if (!Array.isArray(value)) return []
	return value.reduce<GalleryImage[]>((images, entry) => {
		const src = (entry as GalleryImage | null)?.src
		if (typeof src === "string" && src.length > 0) {
			const alt = (entry as GalleryImage).alt
			images.push({ src, alt: typeof alt === "string" ? alt : null })
		}
		return images
	}, [])
}

export const ImageGalleryExtension = Node.create({
	name: IMAGE_GALLERY_NAME,
	group: "block",
	atom: true,
	draggable: true,
	selectable: true,

	addAttributes() {
		return {
			images: {
				default: [] as GalleryImage[],
				parseHTML: (element) => parseGalleryImages(element),
				renderHTML: () => ({}),
			},
		}
	},

	parseHTML() {
		return [{ tag: "figure[data-image-gallery]" }]
	},

	renderHTML({ HTMLAttributes, node }) {
		const images = toGalleryImages(node.attrs.images)
		const slides: DOMOutputSpec[] = images.map((image, index) => [
			"div",
			{ class: "pl-gallery-slide", "data-gallery-index": String(index) },
			[
				"img",
				{
					src: image.src,
					alt: image.alt ?? "",
					class: "pl-gallery-image",
					loading: "lazy",
					draggable: "false",
				},
			],
		])

		return [
			"figure",
			mergeAttributes(HTMLAttributes, {
				"data-image-gallery": "",
				"data-gallery-count": String(images.length),
				class: "pl-gallery",
			}),
			["div", { class: "pl-gallery-track" }, ...slides],
		] as DOMOutputSpec
	},

	addCommands() {
		return {
			setImageGallery:
				({ images }) =>
				({ commands }) => {
					const galleryImages = toGalleryImages(images)
					if (galleryImages.length === 0) return false
					return commands.insertContent({
						type: this.name,
						attrs: { images: galleryImages },
					})
				},
		}
	},
})

// Email clients and Slack blocks have no carousel, so a gallery travels as its
// first image and the rest stay on the page.
export const collapseGalleryToFirstImage = (json: JSONContent): JSONContent => {
	if (!json) return json

	if (json.type === IMAGE_GALLERY_NAME) {
		const first = toGalleryImages(json.attrs?.images)[0]
		if (!first) return { type: "paragraph" }
		return {
			type: "image",
			attrs: { src: first.src, alt: first.alt ?? null },
		}
	}

	if (Array.isArray(json.content)) {
		return {
			...json,
			content: json.content.map((node) => collapseGalleryToFirstImage(node)),
		}
	}

	return json
}
